import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { formatSignedMoney } from "../../lib/money";
import {
  pushRecentTrade,
  readRecentTrades,
} from "../../lib/recentTrades";
import type { components } from "../schema";
import { useInstruments } from "./useInstruments";
import { useTrade } from "./useTrade";
import { type ListTradesQuery, useTrades } from "./useTrades";

type TradeSummary = components["schemas"]["TradeSummaryResponse"];

// Bounded result set: the dropdown lists at most this many rows, newest-first.
// When a search hits the cap we surface a "refine" hint so truncation is never
// silent (see `truncated`).
const RESULT_LIMIT = 25;
const PICKER_LIST_PARAMS = {
  limit: RESULT_LIMIT,
  sort_key: "opened_at",
  sort_order: "desc",
} as const satisfies ListTradesQuery;

export interface TradePickerRow {
  trade_id: string;
  // Resolved primary symbol, or "" when the symbol cannot be resolved yet.
  symbol: string;
  // Opened date "YYYY-MM-DD", or the em-dash for an open/undated trade.
  openedDate: string;
  // Signed, currency-formatted realized P&L, or the em-dash when absent.
  pnl: string;
  kind: "recent" | "result";
}

// Compose the one-line picker label: "SYMBOL · date · P&L". Money is formatted
// only here, at the render edge; it stays a Decimal string on the wire.
export function formatTradePickerLabel(row: {
  symbol: string;
  openedDate: string;
  pnl: string;
}): string {
  return `${row.symbol === "" ? "—" : row.symbol} · ${row.openedDate} · ${row.pnl}`;
}

function pnlFromDecimal(
  amount: string | null | undefined,
  currency: string,
): string {
  if (amount === null || amount === undefined) return "—";
  return formatSignedMoney(amount, { currency });
}

function summaryToRow(summary: TradeSummary, kind: "recent" | "result"): TradePickerRow {
  return {
    trade_id: summary.trade_id,
    symbol: summary.primary_symbol ?? "",
    openedDate: summary.opened_at !== null ? summary.opened_at.slice(0, 10) : "—",
    pnl: pnlFromDecimal(summary.realized_pnl_amount, summary.currency),
    kind,
  };
}

export interface UseTradePickerArgs {
  value: string;
  mode: "create" | "edit";
  debouncedQuery: string;
}

export interface UseTradePickerResult {
  rows: TradePickerRow[];
  isLoading: boolean;
  isError: boolean;
  // True only when a search filled the result cap -- prompts a "refine" hint.
  truncated: boolean;
  // Resolved label for the currently-selected trade when it is NOT in the
  // loaded rows (edit-mode out-of-window resolution); null otherwise.
  selectedLabel: string | null;
  selectedState: "idle" | "loading" | "deleted";
  recordRecent: (id: string) => void;
}

// Data orchestration for <TradePicker>: server-side symbol search, localStorage
// recents resolved against the loaded page, and edit-mode resolution of a
// selected trade that falls outside the search/recents window.
export function useTradePicker({
  value,
  mode,
  debouncedQuery,
}: UseTradePickerArgs): UseTradePickerResult {
  const { user } = useAuth0();
  // Namespace recents per identity so they don't bleed across logins on a
  // shared browser. Falls back to a shared bucket when unauthenticated.
  const namespace = user?.sub ?? "anon";

  const q = debouncedQuery.trim();
  const searching = q.length >= 1;

  // One trades query whose params switch on search state; React Query keys by
  // params, so the displayed data always belongs to the latest query -- no
  // stale-response races to guard manually.
  const listParams: ListTradesQuery = searching
    ? { ...PICKER_LIST_PARAMS, symbol_query: q }
    : { ...PICKER_LIST_PARAMS };
  const tradesQuery = useTrades(listParams);

  const instrumentsQuery = useInstruments();
  const symbolById = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of instrumentsQuery.data?.items ?? []) {
      map.set(i.instrument_id, i.symbol);
    }
    return map;
  }, [instrumentsQuery.data]);

  const items = useMemo<TradeSummary[]>(
    () => tradesQuery.data?.items ?? [],
    [tradesQuery.data],
  );
  const summaryById = useMemo(() => {
    const map = new Map<string, TradeSummary>();
    for (const t of items) map.set(t.trade_id, t);
    return map;
  }, [items]);

  // Edit-mode resolution: only fetch the single trade when it's NOT already in
  // the loaded rows (otherwise reuse the cached summary -- no extra GET). Wait
  // for the list query to settle first, so a trade that IS in the list never
  // triggers a redundant fetch during the loading window.
  const selectedInList = value !== "" && summaryById.has(value);
  const resolveEnabled =
    mode === "edit" && value !== "" && !tradesQuery.isLoading && !selectedInList;
  const selectedDetail = useTrade(resolveEnabled ? value : "");

  const [recentIds, setRecentIds] = useState<string[]>(() =>
    readRecentTrades(namespace),
  );
  useEffect(() => {
    // Re-read when the identity changes (login switch on a shared browser).
    setRecentIds(readRecentTrades(namespace));
  }, [namespace]);

  const recordRecent = useCallback(
    (id: string) => {
      setRecentIds(pushRecentTrade(namespace, id));
    },
    [namespace],
  );

  const rows = useMemo<TradePickerRow[]>(() => {
    // Resolve recents against the loaded (newest-first) page. A recent id absent
    // from the current page is simply not rendered this round -- we do NOT evict
    // it from storage, because "absent from the newest page" is not the same as
    // "deleted" (it may be an older trade). Only resolvable recents render, so
    // there are never phantom rows.
    const recents = recentIds
      .map((id) => summaryById.get(id))
      .filter((s): s is TradeSummary => s !== undefined)
      .map((s) => summaryToRow(s, "recent"));
    const recentIdSet = new Set(recents.map((r) => r.trade_id));
    const results = items
      .filter((t) => !recentIdSet.has(t.trade_id))
      .map((t) => summaryToRow(t, "result"));
    // While searching, recents are noise -- show only the matches.
    return searching ? results : [...recents, ...results];
  }, [recentIds, summaryById, items, searching]);

  let selectedLabel: string | null = null;
  let selectedState: "idle" | "loading" | "deleted" = "idle";
  if (resolveEnabled) {
    if (selectedDetail.isError) {
      selectedState = "deleted";
    } else if (selectedDetail.data !== undefined) {
      const trade = selectedDetail.data;
      selectedLabel = formatTradePickerLabel({
        symbol: symbolById.get(trade.legs[0]?.instrument_id ?? "") ?? "",
        openedDate:
          trade.opened_at !== null ? trade.opened_at.slice(0, 10) : "—",
        pnl: pnlFromDecimal(trade.realized_pnl_amount, trade.currency),
      });
    } else {
      selectedState = "loading";
    }
  } else if (selectedInList) {
    const summary = summaryById.get(value);
    if (summary !== undefined) {
      selectedLabel = formatTradePickerLabel(summaryToRow(summary, "result"));
    }
  }

  return {
    rows,
    isLoading: tradesQuery.isLoading,
    isError: tradesQuery.isError,
    truncated: searching && items.length >= RESULT_LIMIT,
    selectedLabel,
    selectedState,
    recordRecent,
  };
}
